import React from 'react';
import { CalendarBlank, Target, TrendUp, TrendDown, Minus } from '@phosphor-icons/react';

interface CheckInItem {
    id?: string | number;
    // camelCase (from analyticsService)
    goalId?: string | number;
    goalTitle?: string;
    goalStatus?: string;
    progress?: number;
    cycleId?: string | number;
    cycleName?: string;
    cycleTitle?: string;
    cycleType?: string;
    confidenceLevel?: string;
    checkInDate?: string;
    cycleStartDate?: string;
    cycleEndDate?: string;
    count?: number;
    value?: number;
    // Cycle-level fields from API (camelCase)
    flowStatus?: string;
    empStatus?: string;
    mgrStatus?: string;
    checkinOrderCount?: number;
    // snake_case (backwards compatibility)
    goal_id?: string | number;
    goal_title?: string;
    goal_status?: string;
    cycle_id?: string | number;
    cycle_name?: string;
    cycle_title?: string;
    cycle_type?: string;
    confidence_level?: string;
    check_in_date?: string;
    cycle_start_date?: string;
    cycle_end_date?: string;
    // Cycle-level fields from API (snake_case)
    flow_status?: string;
    emp_status?: string;
    mgr_status?: string;
    checkin_order_count?: number;
}

interface CheckInCycleCardProps {
    data: CheckInItem[] | { data: CheckInItem[]; metadata?: any };
    options?: {
        show_progress?: boolean;
        show_confidence?: boolean;
        show_cycle?: boolean;
        max_items?: number;
    };
}

const getConfidenceConfig = (level?: string) => {
    const l = level?.toLowerCase() || '';
    if (l.includes('high') || l === 'on_track' || l === 'on track') {
        return { color: 'bg-green-100 text-green-700', icon: TrendUp, label: 'On Track' };
    }
    if (l.includes('medium') || l === 'at_risk' || l === 'at risk') {
        return { color: 'bg-yellow-100 text-yellow-700', icon: Minus, label: 'At Risk' };
    }
    if (l.includes('low') || l === 'off_track' || l === 'off track') {
        return { color: 'bg-red-100 text-red-700', icon: TrendDown, label: 'Off Track' };
    }
    return { color: 'bg-gray-100 text-gray-700', icon: Minus, label: level || 'Unknown' };
};

const getStatusColor = (status?: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('completed') || s.includes('done')) return 'bg-green-100 text-green-700';
    if (s.includes('progress')) return 'bg-blue-100 text-blue-700';
    if (s.includes('risk')) return 'bg-red-100 text-red-700';
    if (s.includes('not_started') || s.includes('pending')) return 'bg-gray-100 text-gray-700';
    return 'bg-gray-100 text-gray-700';
};

const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return null;
    }
};

export const CheckInCycleCard: React.FC<CheckInCycleCardProps> = ({ data, options = {} }) => {
    const {
        show_progress = true,
        show_confidence = true,
        show_cycle = true,
        max_items = 20
    } = options;

    // Handle case where data might be wrapped in { data: [...] }
    const items: CheckInItem[] = Array.isArray(data) ? data : (data && 'data' in data) ? data.data : [];
    const displayItems = items.slice(0, max_items);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                    <Target size={24} />
                </div>
                <span className="text-sm">No check-ins found</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 p-3 h-full overflow-auto bg-gray-50/50">
            {displayItems.map((item, index) => {
                // Support both camelCase and snake_case, with fallbacks for cycle-level data
                const itemGoalId = item.goalId || item.goal_id || item.cycleId || item.cycle_id;
                // Title: prefer goal title, fall back to cycle title
                const itemGoalTitle = item.goalTitle || item.goal_title || item.cycleTitle || item.cycle_title || '';
                // Status: prefer goal status, fall back to flow status or emp status
                const itemGoalStatus = item.goalStatus || item.goal_status || item.flowStatus || item.flow_status || item.empStatus || item.emp_status || '';
                // Cycle name: prefer cycle name, fall back to cycle title
                const itemCycleName = item.cycleName || item.cycle_name || item.cycleTitle || item.cycle_title || '';
                const itemConfidenceLevel = item.confidenceLevel || item.confidence_level || '';
                const itemCheckInDate = item.checkInDate || item.check_in_date;

                const confidence = getConfidenceConfig(itemConfidenceLevel);
                const ConfidenceIcon = confidence.icon;
                const progress = item.progress ?? 0;
                const checkInDate = formatDate(itemCheckInDate);

                return (
                    <div
                        key={`${itemGoalId}-${index}`}
                        className="group relative bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                    >
                        {/* Header: Goal Title + Status */}
                        <div className="flex justify-between items-start gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                    {itemGoalTitle || 'Unnamed Goal'}
                                </h3>
                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                    {itemGoalStatus && (
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${getStatusColor(itemGoalStatus)}`}>
                                            {itemGoalStatus.replace(/_/g, ' ')}
                                        </span>
                                    )}
                                    {show_confidence && itemConfidenceLevel && (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${confidence.color}`}>
                                            <ConfidenceIcon size={10} weight="bold" />
                                            {confidence.label}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Check-in count badge */}
                            {(() => {
                                const checkInCount = item.count ?? item.value ?? item.checkinOrderCount ?? item.checkin_order_count;
                                if (checkInCount !== undefined && checkInCount > 0) {
                                    return (
                                        <div className="flex-shrink-0 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold">
                                            {checkInCount} check-in{checkInCount > 1 ? 's' : ''}
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        {/* Progress Bar */}
                        {show_progress && (
                            <div className="mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                    <span className="text-xs font-medium text-gray-600 min-w-[32px] text-right">
                                        {progress}%
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Footer: Cycle + Date */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-50 text-xs text-gray-500 flex-wrap gap-2">
                            {show_cycle && itemCycleName && (
                                <div className="flex items-center gap-1">
                                    <Target size={12} />
                                    <span className="truncate max-w-[150px]">{itemCycleName}</span>
                                </div>
                            )}

                            {checkInDate && (
                                <div className="flex items-center gap-1 ml-auto">
                                    <CalendarBlank size={12} />
                                    <span>Last check-in {checkInDate}</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
